import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Platform } from '@ionic/angular';
import { differenceInCalendarDays, addDays } from 'date-fns';

export interface PlannerDateRange {
  startDate: Date;
  endDate: Date;
  numDays: number;
}

export interface PlannerScrollDims {
  scrollLeft: number;
  width: number;
}

export interface PlannerVisibileDates {
  start: Date;
  end: Date;
}

/**
 * Starting column width, in pixels, and the authoritative default for
 * `--day-width`.
 *
 * This used to be read back off the root element's computed style in
 * `setup()`. That read raced Stencil's first render of the enclosing
 * `ion-content`: until that render creates its `<slot>`, `app-planner` is an
 * unassigned light-DOM child of a shadow host, so it sits outside the flat
 * tree with no computed style and the read came back '' -- making the whole
 * planner's day width NaN and silently killing every click, tap and drag.
 * Owning the value here and pushing it *to* the element removes the
 * round-trip, and with it the race.
 *
 * Kept in sync with the `--day-width` fallback in global.scss, which covers
 * painting between element creation and `setup()`.
 */
export const DEFAULT_DAY_WIDTH = 60;

@Injectable({
  providedIn: 'root',
})
export class PlannerUIService {
  private rootEle: HTMLElement = null;
  // tslint:disable-next-line: variable-name
  private _dayWidth = new BehaviorSubject<number>(DEFAULT_DAY_WIDTH);
  // tslint:disable-next-line: variable-name
  private _dateRange = new BehaviorSubject<PlannerDateRange>({
    startDate: new Date(),
    endDate: new Date(),
    numDays: 0,
  });

  private _hilight = new BehaviorSubject<number>(-1);

  private _hoveredJob = new BehaviorSubject<string>('');

  // tslint:disable-next-line: variable-name
  private _scrollDisabled = new BehaviorSubject<boolean>(false);

  // tslint:disable-next-line: variable-name
  private _actionType = new BehaviorSubject<string>('normal');
  mobile = false;

  // tslint:disable-next-line: variable-name
  private _visibleDates = new BehaviorSubject<PlannerVisibileDates>({
    start: null,
    end: null,
  });

  private scrollDimensions: PlannerScrollDims;

  constructor(private platform: Platform) {
    this.mobile = this.platform.is('mobile');
  }

  setup(rootEle: HTMLElement, startDate: Date, endDate: Date) {
    this.rootEle = rootEle;
    // Not routed through the `dayWidth` setter on purpose: that setter also
    // refreshes the visible date range off `scrollDimensions`, which nothing
    // has measured yet this early.
    this.applyDayWidth(DEFAULT_DAY_WIDTH);
    this.dateRange = { startDate, endDate };
    this._actionType.next('normal');
  }

  set scrollDims(dims: PlannerScrollDims) {
    this.scrollDimensions = dims;
    const dayWidth = this._dayWidth.value;
    const startDate = this._dateRange.value.startDate;
    const visibleDates = this._visibleDates.value;
    const leftDays = Math.floor(dims.scrollLeft / dayWidth);
    const rightDays = Math.floor((dims.scrollLeft + dims.width) / dayWidth);
    const newDates: PlannerVisibileDates = {
      start: addDays(startDate, leftDays),
      end: addDays(startDate, rightDays),
    };
    if (
      !visibleDates.start ||
      !visibleDates.end ||
      newDates.start.toDateString() !== visibleDates.start.toDateString() ||
      newDates.end.toDateString() !== visibleDates.end.toDateString()
    ) {
      this._visibleDates.next(newDates);
    }
  }

  set hilight(index: number) {
    this._hilight.next(index);
  }

  set hoveredJob(id: string) {
    this._hoveredJob.next(id);
  }

  get hilight$(): BehaviorSubject<number> {
    return this._hilight;
  }

  get hoveredJob$(): Observable<string> {
    return this._hoveredJob.asObservable();
  }

  get visibleDates$(): BehaviorSubject<PlannerVisibileDates> {
    return this._visibleDates;
  }

  set scrollDisabled(disabled: boolean) {
    this._scrollDisabled.next(disabled);
  }

  get scrollDisabled$(): BehaviorSubject<boolean> {
    return this._scrollDisabled;
  }

  set dayWidth(dayWidth: number) {
    this.applyDayWidth(dayWidth);
    this.scrollDims = this.scrollDimensions;
  }

  getDayWidth(): BehaviorSubject<number> {
    return this._dayWidth;
  }

  /**
   * Publishes the day width and mirrors it onto the root element, so the CSS
   * follows the service rather than the service reading the CSS.
   */
  private applyDayWidth(dayWidth: number) {
    this._dayWidth.next(dayWidth);
    this.rootEle.style.setProperty('--day-width', `${dayWidth}px`);
  }

  set dateRange(dates: { startDate: Date; endDate: Date }) {
    const numDays =
      differenceInCalendarDays(dates.endDate, dates.startDate) + 1;
    this._dateRange.next({ ...dates, numDays });
    this.rootEle.style.setProperty('--num-days', `${numDays}`);
  }

  getDateRange(): BehaviorSubject<PlannerDateRange> {
    return this._dateRange;
  }

  set actionType(newValue: string) {
    this._actionType.next(newValue);
  }

  getActionType(): BehaviorSubject<string> {
    return this._actionType;
  }
}
